import { StyleSheet, Platform } from 'react-native';
import { BREAKPOINTS, getResponsiveValue } from './breakpoints';
import { colors, typography, shadows, spacing, borderRadius } from './theme';

// Ensure spacing values are available by defining fallbacks
const getSpacing = (size) => {
  return spacing[size] || {
    xs: 4,
    sm: 8,
    md: 16,
    lg: 24,
    xl: 32,
    xxl: 48
  }[size] || 16; // Default to 16 if size not found
};

export const adminStyles = StyleSheet.create({
  container: {
    flex: 1,
    width: '100%',
  },
  contentContainer: {
    flexGrow: 1,
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 100,
    ...(Platform.OS === 'web' && {
      boxShadow: 'inset 0px -2px 2px rgba(255, 255, 255, 0.8), 0px 4px 4px rgba(0, 0, 0, 0.5)'
    })
  },
  contentSection: {
    width: '100%',
    maxWidth: 1400,
    padding: getResponsiveValue({
      mobile: 10,
      tablet: 15,
      laptop: 20,
      desktop: 20
    }),
  },
  reportTypeContainer: {
    flexDirection: getResponsiveValue({
      mobile: 'column',
      tablet: 'row',
      laptop: 'row',
      desktop: 'row'
    }),
    justifyContent: 'center',
    alignItems: getResponsiveValue({
      mobile: 'stretch',
      tablet: 'center',
      laptop: 'center',
      desktop: 'center'
    }),
    gap: spacing.md,
    marginBottom: spacing.xl,
    width: '100%',
    padding: getResponsiveValue({
      mobile: spacing.xs,
      tablet: 0,
      laptop: 0,
      desktop: 0
    }),
  },
  reportTypeButton: {
    backgroundColor: '#424b54',
    borderRadius: 10,
    padding: getResponsiveValue({
      mobile: 10,
      tablet: 12,
      laptop: 15,
      desktop: 15
    }),
    minWidth: getResponsiveValue({
      mobile: '100%',
      tablet: 180,
      laptop: 200,
      desktop: 200
    }),
    alignItems: 'center',
    marginBottom: getResponsiveValue({
      mobile: spacing.xs,
      tablet: 0,
      laptop: 0,
      desktop: 0
    }),
  },
  reportTypeActive: {
    backgroundColor: '#2ecc71',
  },
  emptyStateText: {
    color: 'black',
    fontFamily: 'VastagoGrotesk',
    fontSize: 18,
    textAlign: 'center',
    marginTop: 40,
  },
  usersList: {
    display: 'flex',
    flexDirection: getResponsiveValue({
      mobile: 'column',
      tablet: 'row',
      laptop: 'row',
      desktop: 'row'
    }),
    flexWrap: 'wrap',
    gap: getResponsiveValue({
      mobile: 15,
      tablet: 20,
      laptop: 20,
      desktop: 20
    }),
    justifyContent: getResponsiveValue({
      mobile: 'center',
      tablet: 'space-between',
      laptop: 'left',
      desktop: 'left'
    }),
    padding: getResponsiveValue({
      mobile: 10,
      tablet: 15,
      laptop: 20,
      desktop: 20
    }),
    width: '100%',
  },
  userCard: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 10,
    padding: getResponsiveValue({
      mobile: 15,
      tablet: 20,
      laptop: 20,
      desktop: 20
    }),
    width: getResponsiveValue({
      mobile: '100%',
      tablet: '48%',
      laptop: '48%',
      desktop: '48%'
    }),
    minWidth: getResponsiveValue({
      mobile: '100%',
      tablet: 280,
      laptop: 300,
      desktop: 300
    }),
    marginBottom: 20,
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
    backdropFilter: 'blur(10px)',
    position: 'relative',
    borderWidth: 2,
    borderStyle: 'solid',
    borderColor: 'transparent',
  },
  userName: {
    color: colors.textPrimary,
    fontFamily: typography.fontFamily.regular,
    fontSize: typography.size.xl,
    marginBottom: spacing.sm,
  },
  userEmail: {
    color: colors.textSecondary,
    fontFamily: typography.fontFamily.regular,
    fontSize: typography.size.md,
    marginBottom: spacing.xs,
  },
  userType: {
    color: colors.primary,
    fontFamily: typography.fontFamily.regular,
    fontSize: typography.size.sm,
    marginBottom: spacing.xs,
    textTransform: 'uppercase',
  },
  userDetails: {
    color: colors.textPrimary,
    fontFamily: typography.fontFamily.regular,
    fontSize: typography.size.sm,
    marginBottom: spacing.xs,
  },
  userStats: {
    color: colors.textMuted,
    fontFamily: typography.fontFamily.regular,
    fontSize: typography.size.sm,
  },
  userBio: {
    color: colors.textPrimary,
    fontFamily: typography.fontFamily.regular,
    fontSize: typography.size.sm,
    marginBottom: spacing.xs,
  },
  statsSection: {
    marginBottom: getResponsiveValue({
      mobile: 20,
      tablet: 25,
      laptop: 30,
      desktop: 30
    }),
    width: '100%',
  },
  statsTitle: {
    fontFamily: 'DazHandwriting',
    fontSize: getResponsiveValue({
      mobile: 18,
      tablet: 20,
      laptop: 20,
      desktop: 20
    }),
    fontWeight: '700',
    color: 'white',
    marginBottom: getResponsiveValue({
      mobile: 8,
      tablet: 10,
      laptop: 10,
      desktop: 10
    }),
    textAlign: getResponsiveValue({
      mobile: 'center',
      tablet: 'left',
      laptop: 'left',
      desktop: 'left'
    }),
  },
  statsGrid: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: getResponsiveValue({
      mobile: 10,
      tablet: 15,
      laptop: 20,
      desktop: 20
    }),
    justifyContent: getResponsiveValue({
      mobile: 'center',
      tablet: 'center',
      laptop: 'flex-start',
      desktop: 'flex-start'
    }),
  },
  statCard: {
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    borderRadius: 10,
    padding: getResponsiveValue({
      mobile: 10,
      tablet: 15,
      laptop: 20,
      desktop: 20
    }),
    minWidth: getResponsiveValue({
      mobile: '45%',
      tablet: '150px',
      laptop: '180px',
      desktop: '200px'
    }),
    alignItems: 'center',
  },
  statNumber: {
    color: colors.primary,
    fontFamily: typography.fontFamily.regular,
    fontSize: typography.size.xxxl,
    marginBottom: spacing.xs,
  },
  statLabel: {
    color: colors.textPrimary,
    fontFamily: typography.fontFamily.regular,
    fontSize: typography.size.md,
    textAlign: 'center',
  },
  actionSection: {
    display: 'flex',
    flexDirection: getResponsiveValue({
      mobile: 'column',
      tablet: 'column',
      laptop: 'row',
      desktop: 'row'
    }),
    justifyContent: 'center',
    alignItems: 'center',
    gap: getResponsiveValue({
      mobile: 10,
      tablet: 15,
      laptop: 20,
      desktop: 20
    }),
    marginBottom: getResponsiveValue({
      mobile: 20,
      tablet: 25,
      laptop: 30,
      desktop: 30
    }),
    width: '100%',
  },
  testModeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 10,
  },
  testModeButton: {
    backgroundColor: '#424b54',
    borderRadius: 10,
    padding: 10,
    minWidth: 120,
    alignItems: 'center',
  },
  testModeActive: {
    backgroundColor: '#0bb5df',
  },
  testInput: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 8,
    padding: 10,
    color: 'white',
    fontFamily: 'VastagoGrotesk',
    fontWeight: '400',
    fontSize: 16,
    width: 100,
  },
  actionButton: {
    paddingVertical: getResponsiveValue({
      mobile: 10,
      tablet: 12,
      laptop: 15,
      desktop: 15
    }),
    paddingHorizontal: getResponsiveValue({
      mobile: 15,
      tablet: 20,
      laptop: 30,
      desktop: 30
    }),
    borderRadius: 10,
    minWidth: getResponsiveValue({
      mobile: '100%',
      tablet: 120,
      laptop: 150,
      desktop: 150
    }),
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: getResponsiveValue({
      mobile: spacing.xs,
      tablet: 0,
      laptop: 0,
      desktop: 0
    }),
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  actionButtonText: {
    color: 'white',
    fontFamily: 'VastagoGrotesk',
    fontWeight: '500',
    fontSize: getResponsiveValue({
      mobile: 14,
      tablet: 16,
      laptop: 16,
      desktop: 16
    }),
  },
  disabledButton: {
    opacity: 0.5,
  },
  activeButton: {
    backgroundColor: '#2ecc71',
  },
  warningButton: {
    backgroundColor: '#e74c3c',
  },
  progressContainer: {
    width: '100%',
    marginBottom: 20,
  },
  progressText: {
    color: 'white',
    fontSize: 14,
    marginBottom: 5,
  },
  progressBar: {
    width: '100%',
    height: 10,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 5,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#3498db',
    borderRadius: 5,
    transition: 'width 0.3s ease',
  },
  venueList: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 10,
    padding: 20,
    marginBottom: 30,
  },
  venueListTitle: {
    fontFamily: 'DazHandwriting',
    fontSize: 24,
    fontWeight: '700',
    color: 'white',
    marginBottom: 20,
  },
  venueItem: {
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.1)',
    paddingVertical: 10,
  },

  venueLocation: {
    color: 'rgba(255, 255, 255, 0.6)',
    fontFamily: 'DazHandwriting',
    fontSize: 14,
  },
  userActions: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginTop: 20,
    gap: 15,
  },
  maintenanceSection: {
    marginTop: 30,
    padding: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 10,
  },
  passwordPrompt: {
    marginTop: 20,
    alignItems: 'center',
    gap: 15,
  },
  searchFilterContainer: {
    marginBottom: 30,
    width: '100%',
    display: 'flex',
    height: 'auto',
    flexDirection: 'column',
    gap: 15,
  },
  searchInputContainer: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 8,
    padding: 15,
    width: '100%',
  },
  searchInput: {
    backgroundColor: 'transparent',
    color: '#ffffff',
    fontFamily: 'VastagoGrotesk',
    fontSize: 16,
    width: '100%',
    border: 'none',
    outlineStyle: 'none',
  },
  filterButtonsRow: {
    width: '100%',
    paddingHorizontal: 10,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    alignItems: 'center',
  },
  filterButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 8,
    minWidth: getResponsiveValue({
      mobile: 100,
      tablet: 120,
      laptop: 120,
      desktop: 120
    }),
    alignItems: 'center',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      ':hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
      },
    }),
  },
  filterButtonText: {
    color: '#ffffff',
    fontFamily: 'VastagoGrotesk',
    fontSize: 14,
    fontWeight: '500',
  },
  resultsCount: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontFamily: 'VastagoGrotesk',
    fontSize: 14,
    marginTop: 5,
  },
  venueGrid: {
    display: 'grid',
    gridTemplateColumns: getResponsiveValue({
      mobile: 'repeat(1, 1fr)',
      tablet: 'repeat(2, 1fr)',
      laptop: 'repeat(3, 1fr)',
      desktop: 'repeat(4, 1fr)'
    }),
    gap: getResponsiveValue({
      mobile: spacing.sm,
      tablet: spacing.md,
      laptop: spacing.md,
      desktop: spacing.md
    }),
    padding: getResponsiveValue({
      mobile: spacing.sm,
      tablet: spacing.md,
      laptop: spacing.md,
      desktop: spacing.md
    }),
    width: '100%',
  },
  venueCard: {
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    borderRadius: 10,
    height: getResponsiveValue({
      mobile: 250,
      tablet: 280,
      laptop: 300,
      desktop: 300
    }),
    position: 'relative',
    overflow: 'hidden',
    width: '100%',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
      ':hover': {
        transform: 'scale(1.02)',
        boxShadow: '0 12px 20px rgba(0,0,0,0.3)',
      },
    }),
  },
  venueCardBackground: {
    flex: 1,
    width: '100%',
    height: '100%',
    justifyContent: 'flex-end',
  },
  venueCardBackgroundImage: {
    opacity: 0.7,
    resizeMode: 'cover',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  venueCardContent: {
    padding: 20,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: Platform.OS === 'web' ? 'blur(10px)' : undefined,
    width: '100%',
    zIndex: 1,
  },
  venueName: {
    fontFamily: 'VastagoGrotesk',
    fontWeight: '700',
    fontSize: 40,
    color: '#ffffff',
    marginBottom: 5,
    textAlign: 'center',
    textShadow: Platform.OS === 'web' ? '0px 2px 4px rgba(0, 0, 0, 0.5)' : undefined,
  },
  venueLocation: {
    color: 'rgba(255, 255, 255, 0.8)',
    fontFamily: 'VastagoGrotesk',
    fontSize: 14,
    marginBottom: 5,
    textShadow: '0 1px 2px rgba(0,0,0,0.5)',
  },
  venuePhotoCount: {
    color: 'rgba(255, 255, 255, 0.6)',
    fontFamily: 'VastagoGrotesk',
    fontSize: 12,
    marginBottom: 10,
    textShadow: '0 1px 2px rgba(0,0,0,0.5)',
  },
  venueActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 10,
  },
  uploadButtonsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    marginTop: 10,
    zIndex: 2,
  },
  uploadButton: {
    backgroundColor: 'rgba(46, 204, 113, 0.8)',
    borderRadius: 5,
    padding: 8,
    minWidth: 100,
    alignItems: 'center',
    zIndex: 2,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      transition: 'background-color 0.2s ease',
      ':hover': {
        backgroundColor: 'rgba(46, 204, 113, 1)',
      },
    }),
  },
  deleteButton: {
    backgroundColor: 'rgba(231, 76, 60, 0.8)',
    borderRadius: 5,
    padding: 8,
    minWidth: 100,
    alignItems: 'center',
    zIndex: 2,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      transition: 'background-color 0.2s ease',
      ':hover': {
        backgroundColor: 'rgba(231, 76, 60, 1)',
      },
    }),
  },
  uploadButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontFamily: 'VastagoGrotesk',
    fontWeight: '500',
  },
  adminControls: {
    width: '100%',
    flexDirection: getResponsiveValue({
      mobile: 'column',
      tablet: 'row',
      laptop: 'row',
      desktop: 'row'
    }),
    flexWrap: 'wrap',
    gap: getResponsiveValue({
      mobile: 8,
      tablet: 10,
      laptop: 10,
      desktop: 10
    }),
    padding: getResponsiveValue({
      mobile: 10,
      tablet: 15,
      laptop: 20,
      desktop: 20
    }),
    alignItems: 'center',
    justifyContent: 'center',
  },
  controlGroup: {
    marginBottom: 20,
  },
  controlTitle: {
    fontFamily: 'DazHandwriting',
    fontSize: 20,
    fontWeight: '700',
    color: 'white',
    marginBottom: 10,
  },
  controlButton: {
    backgroundColor: 'rgba(46, 204, 113, 0.2)',
    padding: 15,
    borderRadius: 8,
    alignItems: 'center',
    marginBottom: 10,
  },
  buttonText: {
    color: '#ffffff',
    fontFamily: 'VastagoGrotesk',
    fontSize: getResponsiveValue({
      mobile: 14,
      tablet: 16,
      laptop: 16,
      desktop: 16
    }),
  },
  disabledButton: {
    opacity: 0.2,
  },
  activeButton: {
    backgroundColor: '#2ecc71',
  },
  warningButton: {
    backgroundColor: '#e74c3c',
  },
  testControls: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  testInput: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 8,
    padding: 10,
    color: 'white',
    fontFamily: 'VastagoGrotesk',
    fontSize: 16,
    width: 100,
  },
  sectionTitle: {
    color: '#ffffff',
    fontFamily: 'DazHandwriting',
    fontSize: 24,
    fontWeight: '700',
    marginBottom: 20,
  },
  devModeSection: {
    backgroundColor: 'rgba(11, 181, 223, 0.1)',
    borderRadius: 10,
    padding: 20,
    marginBottom: 30,
    borderWidth: 1,
    borderColor: 'rgba(11, 181, 223, 0.3)',
    width: '100%',
    maxWidth: 1200,
    alignSelf: 'center',
  },
  devModeContent: {
    marginTop: 10,
    width: '100%',
  },
  devModeText: {
    color: '#ffffff',
    fontFamily: 'VastagoGrotesk',
    fontWeight: '400',
    fontSize: 16,
    marginBottom: 15,
    opacity: 0.8,
  },
  devModeControls: {
    display: 'flex',
    flexDirection: 'column',
    gap: 15,
    width: '100%',
  },
  devModeSelect: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    color: 'white',
    fontFamily: 'VastagoGrotesk',
    fontWeight: '400',
    fontSize: 16,
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.2)',
    width: '100%',
    marginBottom: 10,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      outlineWidth: 0,
    }),
  },
  selectContainer: {
    width: '100%',
  },
  '@global': {
    '.dev-mode-select': {
      '&::-ms-expand': {
        display: 'none',
      },
    },
  },
  venueImage: {
    width: '100%',
    height: 200,
    borderRadius: 8,
    marginTop: 10,
    marginBottom: 10,
    objectFit: 'cover',
  },
  reportsContainer: {
    width: '100%',
    padding: getSpacing('md'),
  },
  reportsContentContainer: {
    flexGrow: 1,
    alignItems: 'center',
  },
  reportCard: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 10,
    padding: 20,
    marginBottom: 20,
    width: '100%',
    maxWidth: '100%',
    overflow: 'hidden',
  },
  reportHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  headerActions: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 10,
  },
  statusBadge: {
    alignSelf: 'center',
    paddingHorizontal: 20,
    paddingVertical: 8,
    borderRadius: 20,
    color: 'white',
    fontFamily: 'VastagoGrotesk',
    fontSize: 14,
    fontWeight: '500',
  },
  submissionDetails: {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: 8,
    padding: 15,
    marginBottom: 20,
  },
  detailRow: {
    flexDirection: 'row',
    marginBottom: 10,
    flexWrap: 'wrap',
  },
  detailLabel: {
    fontFamily: 'VastagoGrotesk',
    fontWeight: '500',
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.7)',
    width: 120,
    marginRight: 10,
    marginTop: 10,
  },
  detailValue: {
    fontFamily: 'VastagoGrotesk',
    fontWeight: '400',
    fontSize: 14,
    color: '#ffffff',
    flex: 1,
  },
  link: {
    color: colors.primary,
    textDecorationLine: 'underline',
  },
  sectionTitleText: {
    fontFamily: 'VastagoGrotesk',
    fontWeight: '700',
    fontSize: 18,
    color: colors.textPrimary,
  },
  technicalDetails: {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: 8,
    padding: 15,
    marginBottom: 20,
  },
  notes: {
    fontFamily: 'VastagoGrotesk',
    fontWeight: '400',
    fontSize: 14,
    color: colors.textPrimary,
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: 8,
    padding: 15,
    marginBottom: 20,
  },
  actionButtonsContainer: {
    flexDirection: getResponsiveValue({
      mobile: 'column',
      tablet: 'row',
      laptop: 'row',
      desktop: 'row'
    }),
    justifyContent: 'center',
    alignItems: getResponsiveValue({
      mobile: 'stretch',
      tablet: 'center',
      laptop: 'center',
      desktop: 'center'
    }),
    gap: getResponsiveValue({
      mobile: 10,
      tablet: 15,
      laptop: 20,
      desktop: 20
    }),
    marginTop: getResponsiveValue({
      mobile: 15,
      tablet: 20,
      laptop: 20,
      desktop: 20
    }),
    marginBottom: getResponsiveValue({
      mobile: 15,
      tablet: 20,
      laptop: 20,
      desktop: 20
    }),
    width: '100%',
  },
  approveButton: {
    backgroundColor: '#2ecc71',
  },
  rejectButton: {
    backgroundColor: '#e74c3c',
  },
  reportGrid: {
    flexDirection: 'row',
    gap: 20,
    width: '100%',
    paddingTop: 20,
    flexWrap: 'wrap',
  },
  columnLeft: {
    flex: 1,
    minWidth: 300,
    gap: 20,
    maxWidth: '100%',
  },
  columnMiddle: {
    flex: 1,
    minWidth: 300,
    gap: 20,
    maxWidth: '100%',
  },
  columnRight: {
    flex: 1,
    minWidth: 300,
    maxWidth: '100%',
  },
  profileCard: {
    backgroundColor: 'rgba(255,255,255,0.1)',
    borderRadius: 10,
    padding: 15,
    width: '100%',
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
    backdropFilter: 'blur(10px)',
  },
  profileHeader: {
    flexDirection: 'row',
    gap: 15,
  },
  profileImageContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    overflow: 'hidden',
    position: 'relative',
  },
  profileImage: {
    width: '100%',
    height: '100%',
    borderRadius: 40,
  },
  profileInfo: {
    flex: 1,
  },
  submissionCard: {
    backgroundColor: 'rgba(255,255,255,0.1)',
    borderRadius: 10,
    padding: 15,
    width: '100%',
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
    backdropFilter: 'blur(10px)',
  },
  submissionDetails: {
    gap: 10,
  },
  gradesCard: {
    backgroundColor: 'rgba(255,255,255,0.1)',
    borderRadius: 10,
    padding: 15,
    width: '100%',
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
    backdropFilter: 'blur(10px)',
  },
  gradeContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'center',
  },
  gradeBox: {
    alignItems: 'center',
  },
  detailedGradesCard: {
    backgroundColor: 'rgba(255,255,255,0.1)',
    borderRadius: 10,
    padding: 15,
    width: '100%',
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
    backdropFilter: 'blur(10px)',
  },
  gradesGrid: {
    marginTop: 15,
    gap: 10,
  },
  gradeItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 10,
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderRadius: 5,
  },
  venueInfoCard: {
    backgroundColor: 'rgba(255,255,255,0.1)',
    borderRadius: 10,
    padding: 15,
    width: '100%',
    height: '100%',
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
    backdropFilter: 'blur(10px)',
  },
  cardTitle: {
    fontFamily: 'DazHandwriting',
    fontSize: 18,
    fontWeight: '700',
    color: '#ffffff',
    marginBottom: 10,
  },
  userName: {
    fontFamily: 'VastagoGrotesk',
    fontWeight: '700',
    fontSize: 18,
    color: 'white',
    marginBottom: 5,
  },
  userRole: {
    fontFamily: 'VastagoGrotesk',
    fontWeight: '400',
    fontSize: 14,
    color: '#ffffff80',
    marginBottom: 3,
  },
  userWorkplace: {
    fontFamily: 'VastagoGrotesk',
    fontWeight: '400',
    fontSize: 14,
    color: '#ffffff80',
    marginBottom: 3,
  },
  userJoinDate: {
    fontFamily: 'VastagoGrotesk',
    fontWeight: '400',
    fontSize: 12,
    color: '#ffffff60',
  },
  userSubmissions: {
    fontFamily: 'VastagoGrotesk',
    fontWeight: '400',
    fontSize: 12,
    color: '#ffffff60',
  },
  detailText: {
    fontFamily: 'VastagoGrotesk',
    fontWeight: '400',
    fontSize: 14,
    color: 'white',
  },
  mediaSection: {
    padding: 15,
    marginTop: 10,
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    borderRadius: 8,
  },
  mediaTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#ffffff',
    marginBottom: 8,
  },
  mediaScroll: {
    flexDirection: 'row',
    marginTop: 10,
  },
  mediaPreview: {
    width: 100,
    height: 100,
    marginRight: 10,
    borderRadius: 8,
    resizeMode: 'cover',
  },
  gradeLabel: {
    fontFamily: 'VastagoGrotesk',
    fontWeight: '500',
    fontSize: 16,
    color: '#ffffff80',
    marginBottom: 5,
  },
  gradeLetter: {
    color: 'white',
    fontFamily: 'DazHandwriting',
    fontSize: 24,
    fontWeight: '700',
  },
  overallGrade: {
    fontFamily: 'DazHandwriting',
    fontWeight: '700',
    fontSize: 64,
    color: 'white',
    textAlign: 'center',
    marginBottom: 20,
  },
  gradeItemLabel: {
    fontFamily: 'VastagoGrotesk',
    fontWeight: '500',
    fontSize: 14,
    color: 'white',
  },
  gradeItemValue: {
    fontFamily: 'DazHandwriting',
    fontWeight: '700',
    fontSize: 24,
    color: 'white',
  },
  reportCount: {
    fontFamily: 'VastagoGrotesk',
    fontWeight: '400',
    fontSize: 12,
    color: '#ffffff80',
    marginTop: 5,
  },
  venueTitle: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 24,
    fontWeight: '700',
    color: 'white',
    marginBottom: 10,
  },
  venueText: {
    fontFamily: 'VastagoGrotesk',
    fontWeight: '500',
    fontSize: 18,
    color: 'white',
    marginBottom: 10,
  },
  venueHeader: {
    backgroundColor: 'rgba(255,255,255,0.1)',
    borderRadius: 10,
    padding: 15,
    marginBottom: 20,
    width: '100%',
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
    backdropFilter: 'blur(10px)',
  },
  venueHeaderText: {
    fontFamily: 'VastagoGrotesk',
    fontWeight: '700',
    fontSize: 28,
    color: 'white',
    textAlign: 'center',
  },
  userFullName: {
    fontFamily: 'VastagoGrotesk',
    fontWeight: '500',
    fontSize: 16,
    color: '#ffffff',
    marginBottom: 5,
  },
  userEmail: {
    fontFamily: 'VastagoGrotesk',
    fontWeight: '400',
    fontSize: 12,
    color: '#ffffff80',
    marginBottom: 10,
  },
  venueInfoContent: {
    borderRadius: 10,
    padding: 15,
    height: '100%',
  },
  actionButtonsContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 20,
    marginTop: 20,
    marginBottom: 20,
  },
  actionButton: {
    paddingVertical: 15,
    paddingHorizontal: 30,
    borderRadius: 10,
    minWidth: 150,
    alignItems: 'center',
  },
  actionButtonText: {
    color: 'white',
    fontFamily: 'VastagoGrotesk',
    fontWeight: '800',
    fontSize: 16,
  },
  archiveButtonsContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 20,
    marginBottom: 20,
    padding: 10,
  },
  archiveButton: {
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 8,
    minWidth: 150,
  },
  archiveButtonText: {
    color: 'white',
    fontFamily: 'VastagoGrotesk',
    fontWeight: '500',
    fontSize: 16,
    textAlign: 'center',
  },
  statusBadge: {
    paddingVertical: 5,
    paddingHorizontal: 10,
    borderRadius: 5,
    color: 'white',
    fontFamily: 'VastagoGrotesk',
    fontWeight: '500',
    fontSize: 12,
    marginLeft: 10,
  },
  ratingsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 15,
    marginTop: 10,
    marginBottom: 20,
  },
  ratingItem: {
    backgroundColor: 'rgba(255,255,255,0.1)',
    padding: 10,
    borderRadius: 8,
    minWidth: 120,
  },
  ratingLabel: {
    color: 'white',
    fontFamily: 'VastagoGrotesk',
    fontWeight: '500',
    fontSize: 14,
    marginBottom: 5,
  },
  ratingValue: {
    color: '#0bb5df',
    fontFamily: 'VastagoGrotesk',
    fontWeight: '700',
    fontSize: 24,
    textAlign: 'center',
  },
  notesSection: {
    marginTop: 20,
    padding: 15,
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderRadius: 8,
  },
  notesLabel: {
    color: 'white',
    fontFamily: 'VastagoGrotesk',
    fontWeight: '500',
    fontSize: 16,
    marginBottom: 10,
  },
  notesText: {
    fontFamily: 'VastagoGrotesk',
    fontWeight: '400',
    fontSize: 14,
    color: '#ffffff',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: 8,
    padding: 15,
    marginBottom: 20,
  },
  bioSection: {
    padding: 15,
    marginTop: 10,
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    borderRadius: 8,
  },
  bioTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#ffffff',
    marginBottom: 8,
  },
  bioText: {
    fontSize: 14,
    color: '#ffffff',
    lineHeight: 20,
  },
  userCardActive: {
    borderColor: '#2ecc71',
  },
  userCardVerified: {
    borderColor: '#3498db',
  },
  userCardAdmin: {
    borderColor: '#9b59b6',
  },
  userCardMismatch: {
    borderColor: '#f39c12',
  },
  userCardSuspended: {
    borderColor: '#e74c3c',
  },
  fixMismatchButton: {
    backgroundColor: '#f39c12',
    borderRadius: 8,
    padding: 15,
    marginTop: 10,
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      ':hover': {
        backgroundColor: '#f1c40f',
        transform: 'translateY(-2px)',
      }
    }),
  },
  userAnalyticsContainer: {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: 15,
    padding: 25,
    marginBottom: 30,
    width: '100%',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  },
  userAnalyticsGrid: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 20,
    justifyContent: 'center',
    marginBottom: 20,
  },
  userAnalyticCard: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 12,
    padding: 20,
    minWidth: 180,
    flex: 1,
    alignItems: 'center',
    backdropFilter: 'blur(5px)',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    transition: 'transform 0.2s ease-in-out',
    ...(Platform.OS === 'web' && {
      ':hover': {
        transform: 'translateY(-5px)',
      },
    }),
  },
  userAnalyticNumber: {
    color: '#0bb5df',
    fontFamily: 'VastagoGrotesk',
    fontWeight: '700',
    fontSize: 36,
    marginBottom: 8,
    textShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
  },
  userAnalyticLabel: {
    color: 'white',
    fontFamily: 'VastagoGrotesk',
    fontWeight: '500',
    fontSize: 16,
    opacity: 0.9,
  },
  userSearchContainer: {
    flexDirection: 'row',
    gap: 15,
    marginBottom: 30,
    padding: 20,
    width: '100%',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: 15,
    backdropFilter: 'blur(10px)',
  },
  userSearchInput: {
    flex: 1,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 10,
    padding: 15,
    color: 'white',
    fontFamily: 'VastagoGrotesk',
    fontWeight: '400',
    fontSize: 16,
    boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
  },
  legendContainer: {
    flexDirection: 'column',
    gap: 20,
    marginBottom: 10,
    padding: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: 15,
    backdropFilter: 'blur(10px)',
  },
  legendSection: {
    padding: 15,
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: 10,
  },
  legendGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    width: '100%',
    justifyContent: 'flex-start',
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 6,
    minWidth: getResponsiveValue({
      mobile: 100,
      tablet: 120,
      laptop: 120,
      desktop: 120
    }),
  },
  legendColor: {
    width: 16,
    height: 16,
    borderRadius: 4,
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
  legendText: {
    color: 'white',
    fontFamily: 'VastagoGrotesk',
    fontWeight: '400',
    fontSize: 14,
    opacity: 0.9,
  },
  documentationSection: {
    marginTop: 15,
    borderTopWidth: 1,
    borderTopColor: 'rgba(255, 255, 255, 0.1)',
    paddingTop: 15,
  },
  documentationHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 10,
    cursor: 'pointer',
  },
  documentationContent: {
    overflow: 'hidden',
  },
  maintenanceForm: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 8,
    padding: 15,
    marginTop: 10,
  },
  maintenanceInput: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 8,
    padding: 10,
    color: 'white',
    fontFamily: 'VastagoGrotesk',
    fontWeight: '400',
    fontSize: 14,
    marginBottom: 10,
    width: '100%',
  },
  maintenanceSchedule: {
    flexDirection: 'row',
    gap: 10,
    marginBottom: 10,
  },
  maintenanceBanner: {
    backgroundColor: 'rgba(231, 76, 60, 0.9)',
    padding: 15,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
  },
  maintenanceScreen: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
  },
  maintenanceMessage: {
    color: 'white',
    fontFamily: 'VastagoGrotesk',
    fontWeight: '700',
    fontSize: 24,
    textAlign: 'center',
    marginBottom: 20,
  },
  maintenanceTime: {
    color: '#0bb5df',
    fontFamily: 'VastagoGrotesk',
    fontWeight: '500',
    fontSize: 18,
    textAlign: 'center',
  },
  maintenanceScheduleItem: {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: 8,
    padding: 15,
    marginBottom: 10,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 15,
  },
  maintenanceScheduleTime: {
    color: '#0bb5df',
    fontFamily: 'VastagoGrotesk',
    fontWeight: '500',
    fontSize: 16,
    marginBottom: 5,
  },
  maintenanceScheduleDuration: {
    color: 'white',
    fontFamily: 'VastagoGrotesk',
    fontWeight: '400',
    fontSize: 14,
    opacity: 0.8,
    marginBottom: 5,
  },
  maintenanceScheduleMessage: {
    color: 'white',
    fontFamily: 'VastagoGrotesk',
    fontWeight: '400',
    fontSize: 12,
    opacity: 0.6,
  },
  columnSelector: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    padding: 10,
    borderRadius: 8,
    marginBottom: 30,
    alignSelf: 'flex-end',
  },
  columnSelectorButtons: {
    flexDirection: 'row',
    gap: 5,
  },
  columnSelectorLabel: {
    color: 'white',
    fontFamily: 'VastagoGrotesk',
    fontWeight: '500',
    fontSize: 16,
    marginRight: 5,
  },
  columnSelectorButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 8,
    padding: 10,
    minWidth: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.2)',
  },
  columnSelectorButtonActive: {
    backgroundColor: '#0bb5df',
    borderColor: '#0bb5df',
  },
  tabButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 8,
    padding: 15,
    minWidth: 150,
    alignItems: 'center',
    marginRight: 10,
  },
  tabButtonText: {
    color: 'white',
    fontFamily: 'VastagoGrotesk',
    fontWeight: '500',
    fontSize: 16,
  },
  tabButtonActive: {
    backgroundColor: '#0bb5df',
  },
  userStatusText: {
    fontFamily: 'VastagoGrotesk',
    fontWeight: '500',
    fontSize: 14,
    color: 'white',
    marginBottom: 5,
  },
  userTypeText: {
    fontFamily: 'VastagoGrotesk',
    fontWeight: '500',
    fontSize: 14,
    color: '#0bb5df',
    marginBottom: 5,
  },
  experienceText: {
    fontFamily: 'VastagoGrotesk',
    fontWeight: '400',
    fontSize: 14,
    color: '#ffffff',
    lineHeight: 20,
  },
  tabContainer: {
    flexDirection: 'row',
    marginBottom: 30,
    padding: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: 10,
    gap: 10,
  },
  adminTabButton: {
    flex: 1,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 8,
    padding: 15,
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 120,
  },
  adminTabButtonText: {
    color: 'white',
    fontFamily: 'VastagoGrotesk',
    fontWeight: '800',
    fontSize: 16,
    textTransform: 'uppercase',
  },
  adminTabButtonActive: {
    backgroundColor: '#0bb5df',
  },
  loadMoreButton: {
    backgroundColor: 'rgba(11, 181, 223, 0.8)',
    borderRadius: 8,
    padding: getResponsiveValue({
      mobile: 12,
      tablet: 15,
      laptop: 15,
      desktop: 15
    }),
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: getResponsiveValue({
      mobile: 20,
      tablet: 25,
      laptop: 30,
      desktop: 30
    }),
    marginBottom: getResponsiveValue({
      mobile: 20,
      tablet: 25,
      laptop: 30,
      desktop: 30
    }),
    alignSelf: 'center',
    minWidth: getResponsiveValue({
      mobile: '80%',
      tablet: '200px',
      laptop: '200px',
      desktop: '200px'
    }),
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      transition: 'all 0.2s ease-in-out',
      ':hover': {
        backgroundColor: 'rgba(11, 181, 223, 1)',
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
      },
    }),
  },
  loadMoreButtonText: {
    color: 'white',
    fontFamily: 'VastagoGrotesk',
    fontWeight: '500',
    fontSize: getResponsiveValue({
      mobile: 14,
      tablet: 16,
      laptop: 16,
      desktop: 16
    }),
  },
  paginationContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 10,
    marginTop: 20,
    marginBottom: 40,
  },
  paginationButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 8,
    padding: 12,
    minWidth: 40,
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      transition: 'all 0.2s ease-in-out',
      ':hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
        transform: 'translateY(-2px)',
      },
    }),
  },
  paginationButtonActive: {
    backgroundColor: '#0bb5df',
  },
  paginationButtonText: {
    color: 'white',
    fontFamily: 'VastagoGrotesk',
    fontWeight: '500',
    fontSize: 14,
  },
  paginationInfo: {
    color: 'white',
    fontFamily: 'VastagoGrotesk',
    fontWeight: '400',
    fontSize: 14,
    opacity: 0.8,
  },
  reportTitleCard: {
    backgroundColor: 'rgba(255,255,255,0.1)',
    borderRadius: 10,
    padding: 15,
    width: '100%',
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
    backdropFilter: 'blur(10px)',
  },
  submissionsSection: {
    marginBottom: 30,
    width: '100%',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: 10,
    padding: 20,
  },
  submissionCard: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 8,
    padding: 20,
    marginBottom: 15,
  },
  submissionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 15,
  },
  submissionDetails: {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: 8,
    padding: 15,
    marginBottom: 15,
  },
  submissionActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 10,
  },
  adminTitle: {
    fontFamily: 'DazHandwriting',
    fontWeight: '700',
    fontSize: 32,
    color: 'white',
    marginBottom: 20,
    textAlign: 'center',
  },
  gradeA: {
    color: '#4CAF50',
    fontFamily: 'DazHandwriting',
    fontSize: 24,
    fontWeight: '700',
  },
  gradeF: {
    color: '#F44336',
    fontFamily: 'DazHandwriting',
    fontSize: 24,
    fontWeight: '700',
  },
  reportStatus: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 10,
    padding: 15,
    color: 'white',
    fontFamily: 'DazHandwriting',
    fontSize: 16,
    marginBottom: 15,
    width: '90%',
  },
  fileUploadContainer: {
    width: '100%',
    marginBottom: 15,
    padding: 10,
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.1)',
    borderStyle: 'dashed',
  },
  fileUploadLabel: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 16,
    color: 'white',
    marginBottom: 10,
  },
  modeButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 5,
    marginLeft: 10,
  },
  modeButtonActive: {
    backgroundColor: '#3D7FE6',
  },
  modeButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
  },
  importModeDescription: {
    color: 'white',
    marginTop: 8,
  },
  infoContainer: {
    marginTop: 20,
    padding: 15,
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: 8,
    borderLeftWidth: 3,
    borderLeftColor: '#0bb5df',
  },
  infoText: {
    fontFamily: 'VastagoGrotesk',
    fontWeight: '500',
    fontSize: 16,
    color: '#ffffff',
    marginBottom: 10,
  },
  infoList: {
    marginLeft: 10,
  },
  infoListItem: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.8)',
    marginBottom: 5,
    lineHeight: 20,
  },
  techSpecsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
    gap: 15,
    padding: 10,
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: 8,
  },
  techSpecItem: {
    padding: 10,
    borderRadius: 5,
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
  },
  techSpecLabel: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.7)',
    marginBottom: 5,
    fontWeight: '500',
  },
  techSpecValue: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 14,
    color: '#ffffff',
  },
  loadingText: {
    color: '#666',
    fontSize: 16,
    textAlign: 'center',
    marginTop: 20,
  },
  emptyText: {
    color: '#666',
    fontSize: 16,
    textAlign: 'center',
    marginTop: 20,
    fontStyle: 'italic',
  },
  filterContainer: {
    marginBottom: 20,
    width: '100%',
  },
  searchInput: {
    backgroundColor: '#2c2c2c',
    padding: 12,
    borderRadius: 6,
    color: '#fff',
    fontSize: 14,
    marginBottom: 12,
  },
  statusFilters: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  filterButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 4,
    minWidth: 100,
    alignItems: 'center',
  },
  filterButtonActive: {
    backgroundColor: '#0bb5df',
  },
  filterButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '500',
  },
  content: {
    flex: 1,
    width: '100%',
  },
  reportCard: {
    backgroundColor: '#2c2c2c',
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
  },
  reportHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  venueName: {
    color: '#fff',
    fontSize: 18,
    fontWeight: '600',
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  statusBadge: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 4,
    fontSize: 12,
    color: '#fff',
    fontWeight: '500',
  },
  submissionDetails: {
    marginBottom: 16,
  },
  detailRow: {
    flexDirection: 'row',
    marginBottom: 8,
  },
  detailLabel: {
    color: '#aaa',
    width: 120,
    fontSize: 14,
  },
  detailValue: {
    color: '#fff',
    flex: 1,
    fontSize: 14,
  },
  actionButtonsContainer: {
    flexDirection: 'row',
    gap: 12,
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
  },
  actionButton: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 4,
    minWidth: 100,
    alignItems: 'center',
  },
  actionButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  tabButtonsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    marginBottom: 20,
    paddingHorizontal: 20,
  },
  tabButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 8,
    minWidth: 120,
    alignItems: 'center',
  },
  activeTabButton: {
    backgroundColor: '#0bb5df',
  },
  tabButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '500',
  },
  activeTabButtonText: {
    color: 'white',
    fontWeight: '600',
  },
  adminActions: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    marginTop: 20,
  },
  sectionContainer: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 10,
    padding: 20,
    marginBottom: 20,
    width: '100%',
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: 15,
  },
  label: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: 10,
  },
  button: {
    backgroundColor: '#00A575',
    paddingVertical: 10,
    paddingHorizontal: 15,
    borderRadius: 5,
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: 'bold',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: '#222',
    borderRadius: 8,
    padding: 20,
    width: '90%',
    maxWidth: 400,
    borderWidth: 1,
    borderColor: '#444',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: 'white',
    marginBottom: 15,
    textAlign: 'center',
  },
  modalText: {
    fontSize: 14,
    color: '#ccc',
    marginBottom: 15,
    textAlign: 'center',
  },
  passwordInput: {
    backgroundColor: '#333',
    color: 'white',
    borderRadius: 5,
    padding: 12,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: '#444',
  },
  modalButtons: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  modalButton: {
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 5,
    minWidth: 100,
    alignItems: 'center',
  },
  modalButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: 'bold',
  },
  inputRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 10,
    gap: 20,
  },
  inputLabel: {
    color: '#ffffff',
    fontFamily: 'VastagoGrotesk',
    fontWeight: '400',
    fontSize: 18,
    opacity: 0.8,
    marginTop: 30,
  },
  inputField: {
    flex: 1,
  },
  colorPreview: {
    width: 20,
    height: 20,
    borderRadius: 4,
    marginLeft: 10,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.3)',
  },
  stylePageTitle: {
    fontFamily: 'DazHandwriting',
    fontSize: 16,
    fontWeight: '600',
    color: '#ffffff',
  },

  // Site Management Subtabs
  tabContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    marginBottom: 30,
    paddingHorizontal: 20,
    justifyContent: 'center',
  },
  tab: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    minWidth: 120,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.2)',
  },
  activeTab: {
    backgroundColor: '#0bb5df',
    borderColor: '#0bb5df',
  },
  tabText: {
    color: 'rgba(255, 255, 255, 0.8)',
    fontSize: 12,
    fontWeight: '500',
    fontFamily: 'VastagoGrotesk',
  },
  activeTabText: {
    color: 'white',
    fontWeight: '600',
  },

  // Manual Editor Styles
  manualsControls: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 15,
    marginBottom: 20,
    alignItems: 'center',
  },
  searchContainer: {
    flex: 1,
    minWidth: 200,
  },
  searchInput: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    color: 'white',
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
    fontFamily: 'VastagoGrotesk',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.2)',
  },
  manualForm: {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: 10,
    padding: 20,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.1)',
  },
  formTitle: {
    color: 'white',
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 15,
    fontFamily: 'VastagoGrotesk',
  },
  formInput: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    color: 'white',
    borderRadius: 8,
    padding: 12,
    marginBottom: 15,
    fontSize: 14,
    fontFamily: 'VastagoGrotesk',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.2)',
  },
  formButtons: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 10,
  },
  manualsList: {
    marginTop: 20,
  },
  manualsCount: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: 14,
    marginBottom: 15,
    fontFamily: 'VastagoGrotesk',
  },
  manualItem: {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: 8,
    marginBottom: 15,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.1)',
  },
  manualView: {
    flexDirection: 'row',
    padding: 15,
    alignItems: 'center',
  },
  manualInfo: {
    flex: 1,
  },
  manualKeyword: {
    color: '#0bb5df',
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 5,
    fontFamily: 'VastagoGrotesk',
  },
  manualName: {
    color: 'white',
    fontSize: 16,
    fontWeight: '500',
    marginBottom: 5,
    fontFamily: 'VastagoGrotesk',
  },
  manualUrl: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: 12,
    marginBottom: 5,
    fontFamily: 'VastagoGrotesk',
  },
  softwareInfo: {
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: 'rgba(255, 255, 255, 0.1)',
  },
  softwareName: {
    color: '#2ecc71',
    fontSize: 14,
    fontWeight: '500',
    marginBottom: 3,
    fontFamily: 'VastagoGrotesk',
  },
  softwareUrl: {
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: 12,
    fontFamily: 'VastagoGrotesk',
  },
  manualActions: {
    flexDirection: 'row',
    gap: 8,
  },
  manualActionButton: {
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 6,
    minWidth: 60,
    alignItems: 'center',
  },
  manualActionText: {
    color: 'white',
    fontSize: 12,
    fontWeight: '600',
    fontFamily: 'VastagoGrotesk',
  },
  manualEditForm: {
    padding: 15,
  },
  editInput: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    color: 'white',
    borderRadius: 6,
    padding: 10,
    marginBottom: 10,
    fontSize: 14,
    fontFamily: 'VastagoGrotesk',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.2)',
  },
  editButtons: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 10,
  },
  editButton: {
    paddingVertical: 10,
    paddingHorizontal: 15,
    borderRadius: 6,
    minWidth: 80,
    alignItems: 'center',
    flex: 1,
  },

  // Community Moderation Styles
  filtersContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 20,
    marginBottom: 20,
    padding: 15,
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: 8,
    flexWrap: 'wrap',
  },
  filterGroup: {
    minWidth: 200,
    width: "100%",
  },
  filterLabel: {
    color: '#ffffff',
    fontFamily: 'VastagoGrotesk',
    fontSize: 14,
    marginBottom: 5,
    fontWeight: '500',
  },
  searchInput: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 5,
    padding: 10,
    color: '#ffffff',
    fontFamily: 'VastagoGrotesk',
    fontSize: 14,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.2)',
  },
  dropdown: {
    position: 'relative',
  },
  dropdownButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 5,
    padding: 10,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.2)',
  },
  dropdownText: {
    color: '#ffffff',
    fontFamily: 'VastagoGrotesk',
    fontSize: 14,
  },
  refreshButton: {
    backgroundColor: '#2ecc71',
    paddingHorizontal: 15,
    paddingVertical: 8,
    borderRadius: 5,
    alignItems: 'center',
    justifyContent: 'center',
  },
  refreshButtonText: {
    color: '#ffffff',
    fontFamily: 'VastagoGrotesk',
    fontSize: 12,
    fontWeight: '500',
  },
  statsContainer: {
    flexDirection: 'row',
    gap: 15,
    marginBottom: 20,
  },
  statCard: {
    flex: 1,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 8,
    padding: 15,
    alignItems: 'center',
  },
  statNumber: {
    color: '#ffffff',
    fontFamily: 'VastagoGrotesk',
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 5,
  },
  statLabel: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontFamily: 'VastagoGrotesk',
    fontSize: 12,
    textAlign: 'center',
  },
  messagesContainer: {
    flex: 1,
  },
  messageCard: {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: 8,
    padding: 15,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.1)',
  },
  messageHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 10,
  },
  messageInfo: {
    flex: 1,
  },
  venueName: {
    color: '#ffffff',
    fontFamily: 'VastagoGrotesk',
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 2,
  },
  username: {
    color: 'rgba(255, 255, 255, 0.8)',
    fontFamily: 'VastagoGrotesk',
    fontSize: 14,
    marginBottom: 2,
  },
  timestamp: {
    color: 'rgba(255, 255, 255, 0.6)',
    fontFamily: 'VastagoGrotesk',
    fontSize: 12,
  },
  deleteButton: {
    backgroundColor: '#e74c3c',
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  deleteButtonText: {
    color: '#ffffff',
    fontFamily: 'VastagoGrotesk',
    fontSize: 11,
    fontWeight: '500',
  },
  messageText: {
    color: '#ffffff',
    fontFamily: 'VastagoGrotesk',
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 10,
  },
  messageImage: {
    width: 200,
    height: 150,
    borderRadius: 5,
    objectFit: 'cover',
  },
  loadingText: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontFamily: 'VastagoGrotesk',
    fontSize: 14,
    textAlign: 'center',
    padding: 20,
  },
  noDataText: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontFamily: 'VastagoGrotesk',
    fontSize: 14,
    textAlign: 'center',
    padding: 20,
  },

  // Admin Venue Discussion Board Styles
  discussionBoardSection: {
    marginTop: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: 8,
    overflow: 'hidden',
  },
  discussionToggle: {
    padding: 15,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.1)',
  },
  discussionToggleText: {
    color: '#ffffff',
    fontFamily: 'VastagoGrotesk',
    fontSize: 16,
    fontWeight: 'bold',
  },
  discussionContent: {
    padding: 15,
  },
  discussionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 15,
  },
  discussionTitle: {
    color: '#ffffff',
    fontFamily: 'VastagoGrotesk',
    fontSize: 14,
    fontWeight: '500',
    flex: 1,
  },
  messagesScrollView: {
    maxHeight: 400,
  },
  adminMessageCard: {
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    borderRadius: 5,
    padding: 10,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.05)',
  },
  adminMessageHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  adminMessageInfo: {
    flex: 1,
  },
  adminMessageUser: {
    color: '#ffffff',
    fontFamily: 'VastagoGrotesk',
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 2,
  },
  adminMessageTime: {
    color: 'rgba(255, 255, 255, 0.6)',
    fontFamily: 'VastagoGrotesk',
    fontSize: 10,
  },
  adminDeleteButton: {
    backgroundColor: '#e74c3c',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 3,
    alignItems: 'center',
    justifyContent: 'center',
  },
  adminDeleteButtonText: {
    color: '#ffffff',
    fontFamily: 'VastagoGrotesk',
    fontSize: 10,
    fontWeight: '500',
  },
  adminMessageText: {
    color: '#ffffff',
    fontFamily: 'VastagoGrotesk',
    fontSize: 12,
    lineHeight: 16,
    marginBottom: 5,
  },
  adminMessageImage: {
    width: 100,
    height: 75,
    borderRadius: 3,
    objectFit: 'cover',
  },
  noMessagesText: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontFamily: 'VastagoGrotesk',
    fontSize: 12,
    textAlign: 'center',
    padding: 10,
  },

  // Admin header with notification bell
  adminHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },

  // Notification Bell Styles
  notificationContainer: {
    position: 'relative',
    zIndex: 1000,
  },
  notificationBell: {
    position: 'relative',
    padding: 10,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 25,
    alignItems: 'center',
    justifyContent: 'center',
    width: 50,
    height: 50,
    cursor: 'pointer',
    transition: 'background-color 0.3s ease',
    ':hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
    },
  },
  bellIcon: {
    fontSize: 24,
    color: '#ffffff',
  },
  notificationBadge: {
    position: 'absolute',
    top: 5,
    right: 5,
    backgroundColor: '#e74c3c',
    borderRadius: 10,
    minWidth: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 5,
  },
  notificationCount: {
    color: '#ffffff',
    fontFamily: 'VastagoGrotesk',
    fontSize: 10,
    fontWeight: 'bold',
  },
  // Side Panel Overlay
  notificationOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 1000,
  },
  
  // Side Panel
  notificationSidePanel: {
    position: 'fixed',
    top: 0,
    right: 0,
    width: getResponsiveValue({
      mobile: '100vw',
      tablet: '33.33vw',
      laptop: '33.33vw',
      desktop: '33.33vw'
    }),
    height: '100vh',
    backgroundColor: 'rgba(0, 0, 0, 0.95)',
    borderLeftWidth: 1,
    borderLeftColor: 'rgba(255, 255, 255, 0.2)',
    zIndex: 1001,
    display: 'flex',
    flexDirection: 'column',
  },
  notificationPanelHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.1)',
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
  },
  notificationPanelTitle: {
    color: '#ffffff',
    fontFamily: 'VastagoGrotesk',
    fontSize: getResponsiveValue({
      mobile: 18,
      tablet: 20,
      laptop: 20,
      desktop: 20
    }),
    fontWeight: 'bold',
  },
  notificationPanelActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  markReadButton: {
    backgroundColor: '#2ecc71',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
  },
  markReadText: {
    color: '#ffffff',
    fontFamily: 'VastagoGrotesk',
    fontSize: 12,
    fontWeight: '500',
  },
  closePanelButton: {
    backgroundColor: '#e74c3c',
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderRadius: 6,
    width: 30,
    height: 30,
    alignItems: 'center',
    justifyContent: 'center',
  },
  closePanelText: {
    color: '#ffffff',
    fontFamily: 'VastagoGrotesk',
    fontSize: 16,
    fontWeight: 'bold',
  },
  notificationPanelList: {
    flex: 1,
    padding: 10,
  },
  notificationPanelItem: {
    padding: 16,
    marginBottom: 10,
    borderRadius: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.1)',
  },
  notificationVenue: {
    color: '#ffffff',
    fontFamily: 'VastagoGrotesk',
    fontSize: getResponsiveValue({
      mobile: 14,
      tablet: 15,
      laptop: 15,
      desktop: 15
    }),
    fontWeight: 'bold',
    marginBottom: 6,
  },
  notificationMessage: {
    color: 'rgba(255, 255, 255, 0.8)',
    fontFamily: 'VastagoGrotesk',
    fontSize: getResponsiveValue({
      mobile: 12,
      tablet: 13,
      laptop: 13,
      desktop: 13
    }),
    marginBottom: 6,
    lineHeight: getResponsiveValue({
      mobile: 16,
      tablet: 17,
      laptop: 17,
      desktop: 17
    }),
  },
  notificationTime: {
    color: 'rgba(255, 255, 255, 0.6)',
    fontFamily: 'VastagoGrotesk',
    fontSize: getResponsiveValue({
      mobile: 10,
      tablet: 11,
      laptop: 11,
      desktop: 11
    }),
  },
  noNotificationsText: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontFamily: 'VastagoGrotesk',
    fontSize: 12,
    textAlign: 'center',
    padding: 20,
  },
}); 